import {createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {setViewAsEmployeeId} from '@/lib/api'
import {anotarLente} from '@/lib/bitacora-sesion'

export type ViewAsTarget = {id: string; name: string}

type ViewAs = {
  target: ViewAsTarget | null
  viewAs: (target: ViewAsTarget) => void
  exit: () => void
}

const ViewAsContext = createContext<ViewAs>({target: null, viewAs: () => {}, exit: () => {}})

/** Lente de solo lectura (lo impone el backend). La caché se indexa por empresa y no por
 * lente, así que entrar y salir la resetea (KAI-8). */
export function ViewAsProvider({
  active,
  businessId,
  children,
}: {
  active: boolean
  businessId: string | undefined
  children: ReactNode
}) {
  const queryClient = useQueryClient()
  const [target, setTarget] = useState<ViewAsTarget | null>(null)

  useEffect(() => {
    setViewAsEmployeeId(null)
    setTarget(null)
  }, [businessId, active])

  // Un enlace que desmonta al proveedor no pasa por `exit`: el siguiente armazón leería la caché prestada.
  const conLente = useRef(false)
  useEffect(() => {
    conLente.current = target !== null
  }, [target])
  useEffect(
    () => () => {
      if (!conLente.current) return
      setViewAsEmployeeId(null)
      anotarLente(false)
      void queryClient.resetQueries()
    },
    [queryClient]
  )

  const viewAs = useCallback(
    (next: ViewAsTarget) => {
      if (!active) return
      // Set the header BEFORE touching the cache, so the reset's refetch already
      // rides the target's lens (never a stray owner-lens read).
      setViewAsEmployeeId(next.id)
      setTarget(next)
      anotarLente(true)
      // No `clear()`: deja a los observadores montados en un skeleton sin fin. `resetQueries`
      // vuelve todo a pending y refetchea lo montado sin mostrar datos de la lente anterior.
      void queryClient.resetQueries()
    },
    [active, queryClient]
  )

  const exit = useCallback(() => {
    setViewAsEmployeeId(null)
    setTarget(null)
    anotarLente(false)
    void queryClient.resetQueries()
  }, [queryClient])

  return <ViewAsContext.Provider value={{target, viewAs, exit}}>{children}</ViewAsContext.Provider>
}

export function useViewAs(): ViewAs {
  return useContext(ViewAsContext)
}
